/**
 * Used only by SemTextBox NumberInput controls that opt in to "dot as decimal"
 * (ACCEPT_DOT_DECIMAL_CSS_CLASS / [acceptDotDecimal]); other number fields are unchanged.
 * Normalises what a user typed into a decimal number field into the Slovene form the server
 * parses (',' = decimal separator, no grouping). Both '.' and ',' are accepted as the decimal
 * separator:
 *   '13.51477' -> '13,51477'   (one dot, no comma: decimal – never thousands)
 *   '13,51477' -> '13,51477'
 *   '1.234,5'  -> '1234,5'     (dots before the comma: thousands grouping)
 *   '1,234.5'  -> '1234,5'     (commas before the dot: thousands grouping)
 *   '1.234.567'-> '1234567'    (several dots, no comma: thousands grouping)
 * Anything else (several commas, letters, mixed order) is returned unchanged.
 */
export function normalizeDecimalInput(value: string): string {
  if (typeof value !== 'string' || value === '') {
    return value;
  }

  const m = /^(-?)([0-9.,]+)$/.exec(value.trim());
  if (!m) {
    return value;
  }
  const sign = m[1];
  const body = m[2];
  const dots = (body.match(/\./g) || []).length;
  const commas = (body.match(/,/g) || []).length;

  if (dots === 0) {
    return value;
  }
  if (commas === 0) {
    return sign + (dots === 1 ? body.replace('.', ',') : body.replace(/\./g, ''));
  }

  const lastDot = body.lastIndexOf('.');
  const lastComma = body.lastIndexOf(',');
  if (commas === 1 && lastComma > lastDot) {
    return sign + body.replace(/\./g, '');
  }
  if (dots === 1 && lastDot > lastComma) {
    return sign + body.replace(/,/g, '').replace('.', ',');
  }
  return value;
}

/**
 * CSS class that switches a SemTextBox NumberInput control into "dot as decimal" mode.
 * Set it in the control's existing "CssClass" setting; core renders it on the wrapper
 * <div class="SemTextBoxControl ...">, so no core change is needed.
 */
export const ACCEPT_DOT_DECIMAL_CSS_CLASS = 'wg-accept-dot-decimal';

/**
 * Model string for a number loaded from the server (Slovene form the server parses back).
 * Both modes keep every decimal (the old default of toLocaleString rounded to 3 decimals and
 * wrote the rounded value back into the model).
 *  - acceptDot false: exactly the previous string otherwise (grouping, then the first '.' removed).
 *  - acceptDot true: no grouping at all.
 */
export function numberInputModelString(v: number, acceptDot: boolean): string {
  if (acceptDot) {
    return v.toLocaleString('sl-SI', { useGrouping: false, maximumFractionDigits: 20 }).replace('−', '-');
  }
  return v.toLocaleString('sl-SI', { maximumFractionDigits: 20 }).replace('.', '').replace('−', '-');
}

/**
 * What a "dot as decimal" field shows for a model string: the decimal comma as a dot.
 * Values with more than one comma, or that are not strings, are shown as they are.
 */
export function dotDecimalDisplay(value: any): any {
  if (typeof value !== 'string') {
    return value;
  }
  return /^-?[0-9]*,[0-9]*$/.test(value) ? value.replace(',', '.') : value;
}

/** Characters a NumberInput field may hold while typing (a leading minus included). */
export function isNumberInputText(value: string): boolean {
  return /^-?[0-9,.]*$/.test(value);
}

/** Decimal separator keys the NumbersOnly directive lets through. */
export function decimalSeparatorKeys(acceptDot: boolean, decimalSeparator: string): string[] {
  if (acceptDot) {
    return ['.', ',', 'Decimal'];
  }
  return [decimalSeparator == '.' ? '.' : ','];
}

/** Is a decimal separator already in the value (decides whether another may be typed). */
export function hasDecimalSeparator(value: string, acceptDot: boolean, decimalSeparator: string): boolean {
  if (acceptDot) {
    return value.includes('.') || value.includes(',');
  }
  return value.includes(decimalSeparator);
}
